import api, { type RequestOptions } from './api';

export interface MetricResult {
  metric: Record<string, string>;
  value?: [number, string]; // Instant vector
  values?: [number, string][]; // Range vector
}

export interface PrometheusResponse {
  status: string;
  data: {
    resultType: 'matrix' | 'vector' | 'scalar' | 'string';
    result: MetricResult[];
  };
}

export interface LokiResponse {
  status: string;
  data: {
    resultType: 'streams';
    result: {
      stream: Record<string, string>;
      values: [string, string][]; // [timestamp, log line]
    }[];
  };
}

export const observabilityService = {
  /**
   * Query Prometheus metrics (instant)
   */
  async query(query: string, time?: number, options?: RequestOptions): Promise<PrometheusResponse> {
    const params: any = { query };
    if (time) params.time = time;

    return api.get('/observability/metrics/query', { params, ...options });
  },

  /**
   * Query Prometheus metrics over a range
   */
  async queryRange(query: string, start: number, end: number, step: number, options?: RequestOptions): Promise<PrometheusResponse> {
    return api.get('/observability/metrics/query_range', {
      params: {
        query,
        start,
        end,
        step
      },
      ...options
    });
  },

  /**
   * Get label values from Prometheus
   */
  async getLabelValues(label: string): Promise<{ status: string; data: string[] }> {
    return api.get(`/observability/metrics/label/${label}/values`);
  },

  /**
   * Query Loki logs over a range
   */
  async queryLogsRange(query: string, start: number, end: number, limit: number = 100): Promise<LokiResponse> {
    return api.get('/observability/logs/query_range', {
      params: {
        query,
        start: start * 1e9, // Loki expects nanoseconds
        end: end * 1e9,     // Loki expects nanoseconds
        limit
      }
    });
  },

  /**
   * Get label values from Loki
   */
  async getLokiLabelValues(label: string): Promise<{ status: string; data: string[] }> {
    return api.get(`/observability/logs/label/${label}/values`);
  },

  /**
   * Get registered edges from database
   * This is the source of truth - edges register via /api/v1/vsr/ endpoint
   * Returns array of { id, name } objects
   */
  async getRegisteredEdges(options?: RequestOptions): Promise<{
    status: string;
    data: { id: string; name: string; _internalId?: string }[]
  }> {
    return api.get('/observability/edges/ids', options);
  },

  /**
   * Get Grafana dashboard by UID
   */
  async getDashboard(uid: string): Promise<any> {
    return api.get(`/observability/dashboards/uid/${uid}`);
  },

  /**
   * Get batch telemetry for all edges
   * Returns Record<edgeId, { cpu, memory, uptime }>
   */
  async getEdgesTelemetry(options?: RequestOptions): Promise<{
    status: string;
    data: Record<string, { cpu?: number; memory?: number; uptime?: number }>
  }> {
    return api.get('/observability/edges/telemetry', options);
  }
};
